import moment, { Moment } from "moment";
import React, { useEffect, useState } from "react";
import {
  MonthChangeEventHandler,
  DayClickEventHandler,
} from "react-day-picker";
import config from "../config";
import {
  getFormatDate,
  getHoverRangePreview,
  addToDateRange,
} from "./DateRangeSelectorHelper";
import { useDateRangeInputType } from "./types";

const DATE_SELECTOR_VIEW = {
  inputWidth: "100px",
  dateFormat: config.DATE_FORMAT,
};

const DATETIME_SELECTOR_VIEW = {
  inputWidth: "140px",
  dateFormat: config.DATETIME_FORMAT,
};

const FROM_KEY = "from";
const TO_KEY = "to";

export const useInput = ({
  value,
  onChange,
  onBlur,
  isUTC,
  allowOneDayRange,
  allowTimeSelection,
  initialMonth,
  hoverRange: hoverRangeProp,
  allowMultipleHoverRange,
}: useDateRangeInputType) => {
  const dateFormat = allowTimeSelection
    ? DATETIME_SELECTOR_VIEW.dateFormat
    : DATE_SELECTOR_VIEW.dateFormat;

  // The current month shown on the left side of the date range
  const [month, setMonth] = useState(
    initialMonth === FROM_KEY ? value?.from : value?.to
  );
  // The interim date range used to keep track of the first and second selections
  const [localDateRange, setLocalDateRange] = useState<{
    from?: Date;
    to?: Date;
  }>({});

  const [fromInputValue, setFromInputValue] = useState(
    getFormatDate(dateFormat, value?.from, isUTC)
  );
  const [toInputValue, setToInputValue] = useState(
    getFormatDate(dateFormat, value?.to, isUTC)
  );

  const [activeKey, setActiveKey] = useState<typeof FROM_KEY | typeof TO_KEY>(
    FROM_KEY
  );
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isTimePickerOpen, setIsTimePickerOpen] = useState(false);
  const [hoverRange, setHoverRange] = useState<
    { hoverRangeStart?: Date; hoverRangeEnd?: Date } | undefined
  >(undefined);

  const updateFromDate = (date: Date) => {
    setLocalDateRange((dateRange) => ({ from: date, to: dateRange?.to }));
    setFromInputValue(getFormatDate(dateFormat, date, isUTC));
  };

  const updateToDate = (date: Date) => {
    setLocalDateRange((dateRange) => ({ from: dateRange?.from, to: date }));
    setToInputValue(getFormatDate(dateFormat, date, isUTC));
  };

  const updateDate = (dateRange: { from?: Date; to?: Date }) => {
    setLocalDateRange(dateRange);
    setFromInputValue(getFormatDate(dateFormat, dateRange?.from, isUTC));
    setToInputValue(getFormatDate(dateFormat, dateRange?.to, isUTC));
  };

  const resetInput = () => {
    setFromInputValue(getFormatDate(dateFormat, value?.from, isUTC));
    setToInputValue(getFormatDate(dateFormat, value?.to, isUTC));
  };

  const handleDayClick: DayClickEventHandler = (
    selectedDate,
    activeModifiers
  ) => {
    if (!selectedDate || activeModifiers?.disabled) return;

    if (activeModifiers?.hoverRange) {
      const { hoverRangeStart, hoverRangeEnd } =
        getHoverRangePreview(
          selectedDate,
          hoverRangeProp,
          allowMultipleHoverRange,
          localDateRange
        ) ?? {};

      // apply the new date range if it's only the one range
      if (hoverRangeStart && hoverRangeEnd && !allowMultipleHoverRange) {
        updateFromDate(hoverRangeStart);
        updateToDate(hoverRangeEnd);
        return;
      }
    }

    const newDateRange = addToDateRange(
      selectedDate,
      localDateRange,
      allowOneDayRange,
      hoverRangeProp
    );

    if (newDateRange?.from) {
      updateDate(newDateRange);
    }
  };

  const handleMonthChange: MonthChangeEventHandler = (month) => {
    setMonth(month);
  };

  const handleInputClick = (inputKey: typeof FROM_KEY | typeof TO_KEY) => {
    setActiveKey(inputKey);
    setIsPopoverOpen(true);

    // only change month on first open
    if (!isPopoverOpen) {
      setMonth(inputKey === FROM_KEY ? value?.from : value?.to);
      setLocalDateRange({});
    }
  };

  const handleCustomRangeClick = (range: { from: Date; to: Date }) => {
    setMonth(range?.from);
    setActiveKey(FROM_KEY);
    updateDate(range);
  };

  const handleApplyClick = (event?: React.MouseEvent) => {
    // do not pre-emptively close if timepicker being selected
    if (isTimePickerOpen) return;

    // triggers onClickOutside if not stopped
    event?.stopPropagation();

    const newDateRange = { from: localDateRange.from, to: localDateRange.to };

    setIsPopoverOpen(false);
    onBlur?.();

    if (
      moment(newDateRange?.from, dateFormat).isValid() &&
      moment(newDateRange?.to, dateFormat).isValid()
    ) {
      onChange?.(newDateRange as { from: Date; to: Date });
    } else {
      resetInput();
      setMonth(value?.from);
    }

    setLocalDateRange({});
  };

  const handleCancelClick = () => {
    setIsPopoverOpen(false);
    setLocalDateRange(value);
    resetInput();
  };

  const handleOnClickOutside = () => {
    // do not pre-emptively close if timepicker being selected
    if (isTimePickerOpen) return;

    if (isPopoverOpen) {
      onBlur?.();

      handleCancelClick();
    }
  };

  const handleDayMouseEnter = (day: Date) => {
    setHoverRange(
      getHoverRangePreview(
        day,
        hoverRangeProp,
        allowMultipleHoverRange,
        localDateRange
      )
    );
  };

  const handleDayMouseLeave = () => {
    setHoverRange(undefined);
  };

  const handleTimeChange = (
    activeKey: typeof FROM_KEY | typeof TO_KEY,
    time: Moment
  ) => {
    if (activeKey === FROM_KEY) {
      updateFromDate(time.toDate());
      setActiveKey(FROM_KEY);
    }

    if (activeKey === TO_KEY) {
      updateToDate(time.toDate());
      setActiveKey(TO_KEY);
    }
  };

  // If Local values are both set and there's no time value to the date,
  // do the apply process immediately
  useEffect(() => {
    if (
      localDateRange.from &&
      localDateRange.to &&
      isPopoverOpen &&
      !allowTimeSelection
    ) {
      handleApplyClick();
    }
  }, [
    localDateRange.from,
    localDateRange.to,
    isPopoverOpen,
    allowTimeSelection,
  ]);

  const dayPickerProps = {
    month: month,
    onDayClick: handleDayClick,
    onMonthChange: handleMonthChange,
    selected: localDateRange as { from: Date; to: Date },
    modifiers: hoverRangeProp ? hoverRange : undefined,
    onDayMouseEnter: hoverRangeProp ? handleDayMouseEnter : undefined,
    onDayMouseLeave: hoverRangeProp ? handleDayMouseLeave : undefined,
  };

  const fromInputProps = {
    onClick: () => handleInputClick(FROM_KEY),
    value: fromInputValue,
    style: {
      width: allowTimeSelection
        ? DATETIME_SELECTOR_VIEW.inputWidth
        : DATE_SELECTOR_VIEW.inputWidth,
      fontWeight: activeKey === FROM_KEY ? "bold" : "normal",
    },
  };

  const toInputProps = {
    onClick: () => handleInputClick(TO_KEY),
    value: toInputValue,
    style: {
      width: allowTimeSelection
        ? DATETIME_SELECTOR_VIEW.inputWidth
        : DATE_SELECTOR_VIEW.inputWidth,
      fontWeight: activeKey === TO_KEY ? "600" : "400",
    },
  };

  const customRangeProps = {
    onClick: handleCustomRangeClick,
    currentRange: localDateRange,
  };

  const popoverProps = {
    isOpen: isPopoverOpen,
    onClickOutside: handleOnClickOutside,
  };

  const footerProps = {
    currentRange: localDateRange,
    onApplyClick: handleApplyClick,
    onCancelClick: handleCancelClick,
  };

  const timeProps = {
    onTimeChange: handleTimeChange,
    setIsTimePickerOpen,
    from: localDateRange?.from,
    to: localDateRange?.to,
  };

  return {
    dayPickerProps,
    fromInputProps,
    toInputProps,
    customRangeProps,
    popoverProps,
    footerProps,
    timeProps,
  };
};

export default useInput;
